/**
 * PasswordChangeForm — B10.6
 *
 * Inline-Formular fuer Passwort-Aenderung mit RHF+Zod-Validierung.
 * Mindestlaenge 12 Zeichen (Devise-Constraint).
 */
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { useAccountMutations } from './useAccountMutations'

// i18n-Fix (AUTH-Block): Zod-Fehlertexte kamen vormals hartkodiert auf Deutsch,
// obwohl settings.json:password.errors.* bereits DE+EN-Keys dafuer bereithielt.
function buildPasswordSchema(t: (key: string) => string) {
  return z
    .object({
      current_password: z.string().min(1, t('password.errors.currentRequired')),
      new_password: z.string().min(12, t('password.errors.newTooShort')),
      new_password_confirmation: z.string().min(1, t('password.errors.confirmRequired')),
    })
    .refine((d) => d.new_password === d.new_password_confirmation, {
      message: t('password.errors.mismatch'),
      path: ['new_password_confirmation'],
    })
}

type PasswordFormValues = z.infer<ReturnType<typeof buildPasswordSchema>>

type Props = {
  onSuccess: () => void
  changePassword: ReturnType<typeof useAccountMutations>['changePassword']
  isMutating: boolean
}

export function PasswordChangeForm({ onSuccess, changePassword, isMutating }: Props) {
  const { t } = useTranslation('settings')
  const passwordSchema = useMemo(() => buildPasswordSchema(t), [t])
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  })

  async function onSubmit(values: PasswordFormValues) {
    const result = await changePassword(values)
    if (result.ok) {
      reset()
      onSuccess()
      return
    }

    // API-Feldfehler in RHF uebertragen
    if (result.fieldErrors) {
      const fieldMap: Record<string, keyof PasswordFormValues> = {
        current_password: 'current_password',
        new_password: 'new_password',
        new_password_confirmation: 'new_password_confirmation',
      }
      for (const [apiField, formField] of Object.entries(fieldMap)) {
        const msg = result.fieldErrors[apiField]
        if (msg) setError(formField, { message: String(msg) })
      }
    } else {
      setError('current_password', { message: result.message ?? t('password.errors.unknown') })
    }
  }

  const busy = isSubmitting || isMutating

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid="password-change-form" noValidate>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label className="label" htmlFor="current_password">
            {t('password.current')}
          </label>
          <input
            id="current_password"
            type="password"
            className={`input${errors.current_password ? ' input-error' : ''}`}
            autoComplete="current-password"
            {...register('current_password')}
          />
          {errors.current_password && (
            <p className="field-error" role="alert">
              {errors.current_password.message}
            </p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="new_password">
            {t('password.new')}
          </label>
          <input
            id="new_password"
            type="password"
            className={`input${errors.new_password ? ' input-error' : ''}`}
            autoComplete="new-password"
            {...register('new_password')}
          />
          {errors.new_password && (
            <p className="field-error" role="alert">
              {errors.new_password.message}
            </p>
          )}
        </div>

        <div>
          <label className="label" htmlFor="new_password_confirmation">
            {t('password.confirm')}
          </label>
          <input
            id="new_password_confirmation"
            type="password"
            className={`input${errors.new_password_confirmation ? ' input-error' : ''}`}
            autoComplete="new-password"
            {...register('new_password_confirmation')}
          />
          {errors.new_password_confirmation && (
            <p className="field-error" role="alert">
              {errors.new_password_confirmation.message}
            </p>
          )}
        </div>

        <div>
          <button
            type="submit"
            className="btn btn-primary btn-sm"
            disabled={busy}
            aria-disabled={busy}
          >
            {busy ? t('password.submitting') : t('password.submit')}
          </button>
        </div>
      </div>
    </form>
  )
}
